import React, { useState, useEffect, useRef } from "react";

import Notification from "shared-anpost-components/components/notification";
import Uploader from "shared-anpost-components/components/uploader/Uploader";
import FormGroup from "shared-anpost-components/components/form/form_group/FormGroup";
import InputGroup from "shared-anpost-components/components/form/input_group/InputGroup";
import Checkbox from "shared-anpost-components/components/form/checkbox/Checkbox";
import CheckboxLabel from "shared-anpost-components/components/form/checkbox/CheckboxLabel";
import Error from "shared-anpost-components/components/form/fieldError/FieldError";
import { ReactComponent as DownloadSVG } from "shared-anpost-components/images/download.svg";

import { Box, Text, Button, Img } from "../../ui";
import { downloadFile } from "../../../../../helpers/Helpers";
import { checkFileStatus } from "../../../../../services/Api";
import DatabaseCsvSampleImage from "../../../../../images/database_csv_sample.png";

const userProfile = JSON.parse(window.sessionStorage.getItem("userProfile") || "{}");
const uId = userProfile?.uid;

/**
 * @param {{
 *  closeOverlay: () => void,
 *  itemToOrderAgain: Record<string, any>,
 *  handleOrderItemAgain: (customerDataItem: Record<string, any>, isUsingCustomDatabase: boolean, customDatabaseFileId: string) => void
 * }} props
 */

const UploadDatabaseOverlay = ({ closeOverlay, itemToOrderAgain, handleOrderItemAgain }) => {
    const [agreeToTermsAndConditions, setAgreeToTermsAndConditions] = useState(false);
    const [hasUploaded, setHasUploaded] = useState(false);
    const [fileData, setFileData] = useState({
        fileId: "",
        totalAddresses: 0
    });
    const [isVerified, setIsVerified] = useState(false);
    const [errors, setErrors] = useState([]);
    const errorRef = useRef();

    useEffect(() => {
        if (errors.length > 0) errorRef.current.scrollIntoView({ behavior: "smooth" });

        const notificationTimeout = setTimeout(() => {
            if (errors.length) setErrors([]);
        }, 12000);

        return () => clearTimeout(notificationTimeout);
    }, [errors]);

    // make sure user is logged in
    const uploadFileUrl = React.useMemo(() => {
        if (!uId) return "";
        return `${window.DirectMailFileUploadApiUrl}/files/${uId}/target-address`;
    }, []);

    /**
     * @function
     * @param { {fileId: string, uid: string  }[]} data
     */
    const onUploadSuccess = async data => {
        if (!data || !data?.[0]) return;
        try {
            const { fileId } = data[0];
            const res = await checkFileStatus({
                uId,
                fileId,
                productType: "TargetAddress"
            });

            if (res.error) throw new Error(res.error); // TODO: Check this - This throws something weird

            setFileData({
                fileId,
                totalAddresses: res.data?.totalItemsCount
            });
            setIsVerified(true);
            setHasUploaded(true);
            setErrors([]);
        } catch (error) {
            setIsVerified(false);
            setHasUploaded(false);
            if (error) {
                setErrors([
                    error?.message !== "Something went wrong. Please try again later."
                        ? "file-corrupted" // file might be csv but wrong columns so no totalAddresses
                        : "server"
                ]);
            }
        }
    };

    const onUploadError = error => {
        if (error?.type && error?.message) {
            setErrors(["server"]);
        }
    };

    const onRejectedFiles = rejectedFiles => {
        setErrors(rejectedFiles[0]?.errors?.map(error => error.code)); // file is not csv or xlsx - file-invalid-type
    };

    const onContinue = () => {
        const { fileId, totalAddresses } = fileData;
        if (!fileId || !totalAddresses) return;
        if (totalAddresses < 200) {
            setErrors(["min-addresses-required"]);
            setIsVerified(false);
            setHasUploaded(false);
            return;
        }

        handleOrderItemAgain(itemToOrderAgain, true, fileId);
        closeOverlay();
    };

    const resetState = () => {
        setAgreeToTermsAndConditions(false);
        setHasUploaded(false);
        setFileData({
            fileId: "",
            totalAddresses: 0
        });
        setIsVerified(false);
    };

    const handleDownloadSampleCsv = () => {
        downloadFile({
            name: "addresses.xlsx",
            url: `${window.cmsBaseURL}/getapmedia/5dac0b8b-78f4-4f27-94c9-c14c5e143197/addresses.xlsx`
        });
    };

    const UPLOADER_CONTENT = {
        dropFiles: "Drop the files here ...",
        dragAndDrop: "Upload your database",
        browseFile: "Browse file",
        or: "or",
        uploading: "Uploading",
        cancel: "Cancel",
        failedToUpload: "Some files failed to upload"
    };

    const ERROR_CODE_MAP = {
        "file-invalid-type": {
            text: "Uploaded file is not in the correct format.",
            additionalText: "File format must be .csv or .xlsx. Please check your file and try again.",
            color: "red"
        },
        "file-corrupted": {
            text: "File is corrupted or cannot be parsed!",
            additionalText: "Please ensure the headers match: Title, First Name, Last Name, Address 1, Address 2, Address 3, Address 4, Address 5, Eircode.",
            color: "red"
        },
        "min-addresses-required": {
            text: "File is not valid!",
            additionalText: "Please ensure there are at least 200 addresses.",
            color: "red"
        },
        server: {
            text: "A server error occurred.",
            additionalText: "We're sorry, something went wrong. Please try again.",
            color: "yellow"
        },
        default: {
            text: "A server error occurred.",
            additionalText: "We're sorry, something went wrong. Please try again.",
            color: "yellow"
        }
    };

    const isButtonDisabled = !hasUploaded || !agreeToTermsAndConditions;

    return (
        <Box gap={["3.2rem", "4rem"]}>
            {errors.length > 0 && (
                <Box ref={errorRef}>
                    {errors.map(error => (
                        <Notification
                            key={error}
                            colour={ERROR_CODE_MAP[error]?.color || ERROR_CODE_MAP.default.color}
                            text={ERROR_CODE_MAP[error]?.text || ERROR_CODE_MAP.default.text}
                            additionalText={ERROR_CODE_MAP[error]?.additionalText || ERROR_CODE_MAP.default.additionalText}
                        />
                    ))}
                </Box>
            )}
            <Text as="h2" width="100% !important" marginBottom={[0, "1.6rem"]} fontSize={["2.6rem", "3.6rem"]} lineHeight={["3.2rem", "4.5rem"]}>
                Please upload your database
            </Text>
            <Notification
                colour="blue"
                text="File format must be .CSV or .XLSX with columns set out as below in row 1"
                additionalText="Addresses must be located in the Republic of Ireland."
                ctaText="Download sample file"
                ctaCustomIcon={<DownloadSVG />}
                ctaClick={handleDownloadSampleCsv}
                desktopDirection="column"
            />
            <Box overflowX="scroll" paddingBottom={4}>
                <Img src={DatabaseCsvSampleImage} alt="database sample" />
            </Box>
            <Box gap="2.4rem">
                <Box height={["18rem", "30.4rem"]}>
                    <Uploader
                        action={uploadFileUrl}
                        isPrivateRequest
                        dropzoneProps={{
                            accept: {
                                "text/csv": [".csv"],
                                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"]
                            },
                            multiple: false
                        }}
                        onUploadSuccess={onUploadSuccess}
                        onUploadError={onUploadError}
                        onRejectedFiles={onRejectedFiles}
                        onDelete={resetState}
                        onReplace={resetState}
                        isVerified={isVerified}
                        hasError={errors.length > 0}
                        content={UPLOADER_CONTENT}
                    />
                </Box>
                <Box>
                    <FormGroup>
                        <InputGroup>
                            <Checkbox
                                id="termsAndConditions"
                                name="termsAndConditions"
                                ariaInvalid={false ? "true" : "false"}
                                ariaDescribedby="termsAndConditionsError"
                                change={e => setAgreeToTermsAndConditions(e.target.checked)}
                                value={agreeToTermsAndConditions}
                                checked={agreeToTermsAndConditions}
                                required
                            />
                            <CheckboxLabel
                                htmlFor="termsAndConditions"
                                text={[
                                    "I am authorised to bind the company that I have registered on behalf of for this service including the data processing terms and I agree to the ",
                                    <a
                                        key="1"
                                        href="/Terms-Conditions" // TODO: update link
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Terms & Conditions.
                                    </a>
                                ]}
                            />
                        </InputGroup>
                    </FormGroup>
                </Box>
            </Box>
            <Box gap={["2.4rem", "4rem"]}>
                <Button type="button" width={["100%", "170px"]} className="gtm-cta" isDisabled={isButtonDisabled} onClick={onContinue}>
                    Continue
                </Button>
            </Box>
        </Box>
    );
};

export default UploadDatabaseOverlay;
